import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/**
 * Minimal link contract so any router's link component can be plugged into
 * the design system (rows, tables, navigation).
 */
export interface NavLinkComponentProps {
  href: string;
  className?: string | undefined;
  'aria-current'?: 'page';
  'aria-label'?: string;
  onClick?: () => void;
  /**
   * How the page change looks (View Transitions): `push` slides the new page in
   * from the right on phones, `pop` slides it out to the right; without it
   * (tabs, sidebar) the pages cross-fade.
   */
  transition?: 'push' | 'pop';
  children: ReactNode;
}

function DefaultLink({ href, children, transition, ...rest }: NavLinkComponentProps) {
  return (
    <a href={href} data-transition={transition} {...rest}>
      {children}
    </a>
  );
}

const LinkContext = createContext<ComponentType<NavLinkComponentProps>>(DefaultLink);

/** Registers the app's router link once; defaults to a plain `<a>`. */
export function LinkProvider({
  component,
  children,
}: {
  component: ComponentType<NavLinkComponentProps>;
  children: ReactNode;
}) {
  return <LinkContext.Provider value={component}>{children}</LinkContext.Provider>;
}

/** The link component to render internal links with. */
export function useLinkComponent(
  override?: ComponentType<NavLinkComponentProps>,
): ComponentType<NavLinkComponentProps> {
  const fromContext = useContext(LinkContext);
  return override ?? fromContext;
}
